// lib/quiz-definition.ts

export interface QuizAnswer {
  text: string;
  nextStepId: string;
}

export interface QuizStep {
  id: string;
  type: 'question' | 'statement' | 'precheckout' | 'checkout' | 'loading';
  title?: string;
  content: string; 
  imageUrl?: string;
  answers?: QuizAnswer[];
  nextStepId?: string;
  skipToStepId?: string;
  requiresEmailForm?: boolean;
}

export const quizDefinition: QuizStep[] = [
{
  id: "start",
  type: "question",
  content: "How do you usually find new spots?",
  answers: [
    { text: "I ask friends", nextStepId: "statement_friends" },
    { text: "I search Google", nextStepId: "statement_google" },
    { text: "Instagram or TikTok", nextStepId: "statement_social" },
    { text: "I just wander", nextStepId: "statement_walk" }
  ],
  skipToStepId: "q_context"
},
{
  id: "statement_friends",
  type: "statement",
  title: "Friends Know Best — Kinda",
  content: "Friends are a great source – in fact, over 70% of people trust recommendations from family and friends most. Still, even your BFFs haven't been everywhere… 😉",
  imageUrl: "/images/quiz/friends.svg",
  nextStepId: "q_context"
},
{
  id: "statement_google",
  type: "statement",
  title: "Scroll Fatigue is Real",
  content: "Endless scrolling on Google? Yep, we’ve been there. Atly skips the fluff and shows you only the best of the best. ✨",
  imageUrl: "/images/quiz/google.svg",
  nextStepId: "q_context"
},
{
  id: "statement_social",
  type: "statement",
  title: "Social Feeds ≠ Real Vibes",
  content: "Social media can inspire, but it doesn't always get you what *you* need. It's hard to tell which spots are actually your vibe from a quick post.",
  imageUrl: "/images/quiz/social.svg",
  nextStepId: "q_context"
},
{
  id: "statement_walk",
  type: "statement",
  title: "Adventurous Spirit!",
  content: "Sometimes wandering leads to gems, but it's easy to miss the good stuff. What if you had a curated map always ready?",
  imageUrl: "/images/quiz/walk.svg",
  nextStepId: "q_context"
},
{
  id: "q_context",
  type: "question",
  content: "Where do you usually want to find new places?",
  answers: [
    { text: "When traveling", nextStepId: "statement_travel" },
    { text: "When at home", nextStepId: "statement_home" },
    { text: "All the time 😅", nextStepId: "statement_always" }
  ],
  skipToStepId: "q_priority"
},
{
  id: "statement_travel",
  type: "statement",
  title: "Travel Like a Local",
  content: "Being in a new place is exciting, but finding great spots away from home can be tricky. Atly acts like your local friend in every city.",
  imageUrl: "/images/quiz/travel.svg",
  nextStepId: "q_priority"
},
{
  id: "statement_home",
  type: "statement",
  title: "Stuck in a Rut?",
  content: "Totally get it — 75% of Atly users say it's even harder to find great spots near home. We've got you covered.",
  imageUrl: "/images/quiz/home.svg",
  nextStepId: "q_priority"
},
{
  id: "statement_always",
  type: "statement",
  title: "A True Explorer!",
  content: "You deserve better than basic suggestions — let's personalize your map to match your vibe.",
  imageUrl: "/images/quiz/explorer.svg",
  nextStepId: "q_priority"
},
{
  id: "q_priority",
  type: "question",
  content: "What's most important when choosing a place?",
  answers: [
    { text: "The vibe", nextStepId: "statement_vibe" },
    { text: "Food & drinks", nextStepId: "statement_food" },
    { text: "Not too busy", nextStepId: "statement_balance" }
  ],
  skipToStepId: "q_saved"
},
{
  id: "statement_vibe",
  type: "statement",
  title: "It's a Vibe Thing",
  content: "Yes! Atly's AI reads reviews and filters places by keywords like cozy, romantic, or lively so you get your kind of vibe.",
  imageUrl: "/images/quiz/vibe.svg",
  nextStepId: "q_saved"
},
{
  id: "statement_food",
  type: "statement",
  title: "Flavors First",
  content: "Let's be honest — good food is non-negotiable. Atly curates the best-reviewed menus, dishes, and drinks.",
  imageUrl: "/images/quiz/food.svg",
  nextStepId: "q_saved"
},
{
  id: "statement_balance",
  type: "statement",
  title: "Balance is Everything",
  content: "Totally! The sweet spot between quiet and fun is tough to find — that's where Atly shines.",
  imageUrl: "/images/quiz/balance.svg",
  nextStepId: "q_saved"
},
{
  id: "q_saved",
  type: "question",
  content: "Ever saved a place… and forgot to go?",
  answers: [
    { text: "All the time 😂", nextStepId: "statement_saved_fail" },
    { text: "My list is chaos", nextStepId: "statement_saved_fail" },
    { text: "I'm organized", nextStepId: "statement_saved_ok" },
    { text: "I don't save stuff", nextStepId: "statement_saved_none" }
  ],
  skipToStepId: "q_map_type"
},
{
  id: "statement_saved_fail",
  type: "statement",
  title: "We’ve Been There",
  content: "Same! Only 13% of people actually visit the places they save. With Atly, your favorite spots stay on a smart, easy-to-access map.",
  imageUrl: "/images/quiz/saved_fail.svg",
  nextStepId: "q_map_type"
},
{
  id: "statement_saved_ok",
  type: "statement",
  title: "Organized Explorer",
  content: "Nice! You're ahead of the game. Atly just makes it even easier — your personalized map, always ready to go.",
  imageUrl: "/images/quiz/saved_ok.svg",
  nextStepId: "q_map_type"
},
{
  id: "statement_saved_none",
  type: "statement",
  title: "Save Smarter",
  content: "Fair enough. With Atly, you don't need to — your personalized map updates automatically as you explore.",
  imageUrl: "/images/quiz/saved_none.svg",
  nextStepId: "q_map_type"
},
{
  id: "q_map_type",
  type: "question",
  content: "What kind of map would you want?",
  answers: [
    { text: "Hidden gems", nextStepId: "cta_hidden" },
    { text: "Top-rated spots", nextStepId: "cta_best" },
    { text: "My vibe", nextStepId: "cta_vibe" },
    { text: "A mix of all", nextStepId: "cta_mix" }
  ],
  skipToStepId: "final_cta"
},
{
  id: "cta_hidden",
  type: "statement",
  title: "You Love Hidden Gems",
  content: "You're all about those off-the-radar gems 🔎 — Atly's got your map ready. Just one step left!",
  imageUrl: "/images/quiz/hidden.svg",
  nextStepId: "final_cta"
},
{
  id: "cta_best",
  type: "statement",
  title: "Best of the Best",
  content: "Say no more — your curated list of top-rated spots is waiting. Let's get your map set up.",
  imageUrl: "/images/quiz/best.svg",
  nextStepId: "final_cta"
},
{
  id: "cta_vibe",
  type: "statement",
  title: "Vibe-Based Magic",
  content: "Whether it's candlelit pasta or chill espresso bars, your vibe is the guide. Grab your personalized map!",
  imageUrl: "/images/quiz/vibe_map.svg",
  nextStepId: "final_cta"
},
{
  id: "cta_mix",
  type: "statement",
  title: "A Little Bit of Everything",
  content: "You want it all — and we've got it. Let's load up your personalized mix of places!",
  imageUrl: "/images/quiz/mix.svg",
  nextStepId: "final_cta"
},
{
  id: "final_cta",
  type: "statement",
  title: "You're All Set!",
  content: "Your personalized map is ready in Atly. Start your free trial now and unlock your new go-to spots.",
  imageUrl: "/images/quiz/final_cta.svg",
  nextStepId: "loading_results"
},
{
  id: "loading_results",
  type: "loading",
  content: "Calculating your results...",
  nextStepId: "precheckout"
},
{
  id: "precheckout",
  type: "precheckout",
  content: "Time to unlock personalized recommendations!",
  nextStepId: "checkout"
},
{
  id: "checkout",
  type: "checkout",
  content: "Checkout Placeholder"
},
{
  id: "post_checkout_full",
  type: "statement",
  title: "We're Full!",
  content: "We're sorry, we're full right now. Don't worry, we didn't charge you. Please enter an email and we will let you know when a place opens.",
  requiresEmailForm: true
}
];

export const getQuizStep = (id: string): QuizStep | undefined => {
return quizDefinition.find(step => step.id === id);
};
